import type { Experiment } from "@andrum/schemas";
import { t } from "./index";

/**
 * Always resolve experiment copy from i18n by slug.
 * Never surface raw server/Swedish catalog strings when keys exist.
 * Fallback chain: active locale → English key → original exp field.
 */
function tCatalog(key: string, fallback: string): string {
  const value = t(key);
  if (!value || value === key || value.startsWith("catalog.")) {
    // Try English explicitly is handled by i18n fallbackLng: "en"
    return fallback;
  }
  return value;
}

export function localizeExperiment(exp: Experiment): Experiment {
  const base = `catalog.${exp.slug}`;
  const title = tCatalog(`${base}.title`, exp.title);
  const summary = tCatalog(`${base}.summary`, exp.summary);
  const rationale = tCatalog(`${base}.rationale`, exp.rationale);
  const smallerVersion = tCatalog(`${base}.smaller`, exp.smallerVersion);

  const instructions = [1, 2, 3, 4, 5]
    .map((i) => {
      const key = `${base}.i${i}`;
      const value = t(key);
      if (!value || value === key || value.startsWith("catalog.")) return null;
      return value;
    })
    .filter((s): s is string => Boolean(s));

  const reflectionQuestions = [1, 2, 3, 4]
    .map((i) => {
      const key = `${base}.r${i}`;
      const value = t(key);
      if (!value || value === key || value.startsWith("catalog.")) return null;
      return value;
    })
    .filter((s): s is string => Boolean(s));

  return {
    ...exp,
    title,
    summary,
    rationale,
    instructions: instructions.length > 0 ? instructions : exp.instructions,
    smallerVersion,
    reflectionQuestions:
      reflectionQuestions.length > 0
        ? reflectionQuestions
        : exp.reflectionQuestions,
  };
}

export function localizeExperiments(list: Experiment[]): Experiment[] {
  return list.map(localizeExperiment);
}

export function scienceNoteForSlug(slug: string): {
  why: string;
  know: string;
  dont: string;
} | null {
  const base = `catalog.${slug}`;
  const why = t(`${base}.why`);
  if (!why || why === `${base}.why` || why.startsWith("catalog.")) {
    return null;
  }
  return {
    why,
    know: t(`${base}.know`),
    dont: t(`${base}.dont`),
  };
}

export function reasonLabel(code: string): string {
  const key = `experiment.reasons.${code}`;
  const value = t(key);
  if (!value || value === key || value.startsWith("experiment.reasons.")) {
    return t("experiment.reasons.default");
  }
  return value;
}
